import React, { useEffect, useState } from "react";
import { Lightbulb, ArrowRight, CaretRight } from "@phosphor-icons/react";
import { getStory } from "../demo/stories";

/**
 * Iter 20.10 — DemoStoryPanel
 *
 * Persistentes linkes Panel im Split-Screen-Modus der Demo. Zeigt einen
 * schritt-abhängigen Erklärungsblock mit Titel, Zusammenfassung, Bullet-Liste
 * und Klick-Hinweis. Wird via `window`-CustomEvent `et:demo-step-change`
 * über den aktuellen Wizard-Schritt informiert.
 */
export default function DemoStoryPanel({ caseId }) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const onStepChange = (evt) => {
      const next = typeof evt?.detail === "number" ? evt.detail : 0;
      setStep(next);
    };
    window.addEventListener("et:demo-step-change", onStepChange);
    return () => window.removeEventListener("et:demo-step-change", onStepChange);
  }, []);

  const story = getStory(caseId);
  if (!story) return null;

  const panels = story.storyPanels;
  const panel =
    panels[Math.min(Math.max(step, 0), panels.length - 1)] || panels[0];

  return (
    <aside
      className="lg:sticky lg:top-6 lg:h-fit"
      data-testid="demo-story-panel"
    >
      <div className="border border-ink-200 bg-white rounded-sm overflow-hidden">
        {/* Progress-Header */}
        <div className="border-b border-ink-200 bg-ink-50 px-5 py-3">
          <div className="flex items-center gap-2">
            {panels.map((_, i) => (
              <div
                key={i}
                className={[
                  "h-1.5 rounded-sm transition-all",
                  i === step
                    ? "flex-[2] bg-brand-500"
                    : i < step
                    ? "flex-1 bg-brand-300"
                    : "flex-1 bg-ink-200",
                ].join(" ")}
              />
            ))}
          </div>
          <div className="mt-2 text-[10px] uppercase tracking-widest text-warm-500 font-semibold">
            Schritt {step + 1} von {panels.length}
          </div>
        </div>

        {/* Content */}
        <div className="p-5" key={step}>
          <div className="flex items-start gap-2 mb-3">
            <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-sm border border-brand-300 bg-brand-50">
              <Lightbulb size={12} weight="duotone" className="text-brand-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3
                className="font-cabinet text-lg tracking-tight text-ink-900 font-medium leading-tight"
                data-testid="demo-story-panel-title"
              >
                {panel.title}
              </h3>
            </div>
          </div>

          <p className="text-[13px] text-ink-800 leading-relaxed mb-4">
            {panel.summary}
          </p>

          {panel.bullets && panel.bullets.length > 0 && (
            <ul className="space-y-1.5 mb-4">
              {panel.bullets.map((b, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-[12px] text-warm-700 leading-snug"
                >
                  <CaretRight
                    size={11}
                    weight="bold"
                    className="mt-0.5 shrink-0 text-brand-500"
                  />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}

          {panel.hint && (
            <div
              className="mt-2 border-l-2 border-brand-400 bg-brand-50/60 rounded-sm px-3 py-2 text-[12px] text-ink-900 leading-snug flex items-start gap-2"
              data-testid="demo-story-panel-hint"
            >
              <ArrowRight
                size={11}
                weight="bold"
                className="mt-0.5 shrink-0 text-brand-600"
              />
              <span>{panel.hint}</span>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
